import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
    Box,
    CssBaseline,
    Divider,
    Drawer as MuiDrawer,
    IconButton,
    Toolbar,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { AppBar, drawerMiniWidth, drawerWidth } from "./styles";
import AccountMenu from "./AccountMenu";
import { DashboardLayoutContext } from "./DashboardLayoutContext";
import { usePWA } from "../hooks/usePWA";

const SIDEBAR_DESKTOP_OPEN_KEY = "dashboard.sidebar.desktopOpen.v2";

function readStoredDesktopOpen(fallback) {
    try {
        const stored = localStorage.getItem(SIDEBAR_DESKTOP_OPEN_KEY);
        if (stored === "0") return false;
        if (stored === "1") return true;
    } catch {
        /* ignore */
    }
    return fallback;
}

/**
 * Responsive admin / student / teacher shell: temporary nav drawer on mobile, permanent on desktop.
 */
export default function DashboardShell({
    title,
    sidebar,
    children,
    defaultDesktopOpen = true,
    headerExtra = null,
}) {
    const theme = useTheme();
    const { isStandalone, preferAppLayout } = usePWA();
    const useAppLayout = isStandalone && preferAppLayout;
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const location = useLocation();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [desktopOpen, setDesktopOpen] = useState(() => readStoredDesktopOpen(defaultDesktopOpen));

    const closeMobileNav = () => setMobileOpen(false);
    const persistDesktopOpen = (next) => {
        try {
            localStorage.setItem(SIDEBAR_DESKTOP_OPEN_KEY, next ? "1" : "0");
        } catch {
            /* ignore */
        }
    };
    const toggleDrawer = () => {
        if (isMobile) setMobileOpen((v) => !v);
        else
            setDesktopOpen((v) => {
                const next = !v;
                persistDesktopOpen(next);
                return next;
            });
    };
    const toggleDesktopNav = () =>
        setDesktopOpen((v) => {
            const next = !v;
            persistDesktopOpen(next);
            return next;
        });

    useEffect(() => {
        closeMobileNav();
    }, [location.pathname]);

    useEffect(() => {
        if (!isMobile) {
            closeMobileNav();
        }
    }, [isMobile]);

    const drawerNav = (
        <>
            <Toolbar
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    px: 1,
                    minHeight: { xs: 56, sm: 64 },
                }}
            >
                {isMobile ? (
                    <IconButton onClick={toggleDrawer} aria-label="Close menu">
                        <ChevronLeftIcon />
                    </IconButton>
                ) : (
                    <IconButton onClick={toggleDesktopNav} aria-label={desktopOpen ? "Collapse menu" : "Expand menu"}>
                        <ChevronLeftIcon
                            sx={{
                                transform: desktopOpen ? "none" : "rotate(180deg)",
                                transition: (t) => t.transitions.create("transform"),
                            }}
                        />
                    </IconButton>
                )}
            </Toolbar>
            <Divider />
            <Box
                component="nav"
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflow: "auto",
                    display: "flex",
                    flexDirection: "column",
                    WebkitOverflowScrolling: "touch",
                }}
            >
                {sidebar}
            </Box>
        </>
    );

    const permanentWidth = desktopOpen ? drawerWidth : drawerMiniWidth;
    const appBarOpen = !isMobile;
    const navCollapsed = !isMobile && !desktopOpen;

    return (
        <DashboardLayoutContext.Provider
            value={{ closeMobileNav, desktopNavOpen: desktopOpen, toggleDesktopNav, navCollapsed }}
        >
            <Box sx={{ display: "flex", width: "100%", minHeight: "100vh" }}>
                <CssBaseline />
                <AppBar
                    open={appBarOpen}
                    position="fixed"
                    className={useAppLayout ? "pwa-app-bar" : undefined}
                    sx={{
                        ...(useAppLayout && {
                            paddingTop: "env(safe-area-inset-top)",
                        }),
                        ...(appBarOpen && {
                            [theme.breakpoints.up("md")]: {
                                marginLeft: `${permanentWidth}px`,
                                width: `calc(100% - ${permanentWidth}px)`,
                            },
                        }),
                    }}
                >
                    <Toolbar
                        sx={{
                            px: { xs: 1.5, sm: 2 },
                            pr: { xs: 1.5, sm: 2, md: 3 },
                            gap: 1,
                            minHeight: { xs: 56, sm: 64 },
                        }}
                    >
                        <IconButton
                            edge="start"
                            color="inherit"
                            aria-label="Open menu"
                            onClick={toggleDrawer}
                            sx={{
                                mr: { xs: 0.5, sm: 1 },
                                display: { md: "none" },
                            }}
                        >
                            <MenuIcon />
                        </IconButton>
                        <Typography
                            component="h1"
                            variant="h6"
                            color="inherit"
                            noWrap
                            sx={{
                                flexGrow: 1,
                                fontWeight: 600,
                                fontSize: { xs: "0.95rem", sm: "1.1rem", md: "1.25rem" },
                            }}
                        >
                            {title}
                        </Typography>
                        {headerExtra}
                        <AccountMenu />
                    </Toolbar>
                </AppBar>

                <MuiDrawer
                    variant="temporary"
                    open={isMobile && mobileOpen}
                    onClose={closeMobileNav}
                    ModalProps={{ keepMounted: true }}
                    sx={{
                        display: { xs: "block", md: "none" },
                        "& .MuiDrawer-paper": {
                            width: drawerWidth,
                            maxWidth: "88vw",
                            boxSizing: "border-box",
                            overflow: "hidden",
                            overflowX: "hidden",
                        },
                    }}
                >
                    {drawerNav}
                </MuiDrawer>

                <MuiDrawer
                    variant="permanent"
                    open={desktopOpen}
                    sx={{
                        display: { xs: "none", md: "flex" },
                        width: permanentWidth,
                        flexShrink: 0,
                        transition: (t) =>
                            t.transitions.create("width", {
                                easing: t.transitions.easing.sharp,
                                duration: t.transitions.duration.enteringScreen,
                            }),
                        "& .MuiDrawer-paper": {
                            position: "relative",
                            width: permanentWidth,
                            overflow: "hidden",
                            overflowX: "hidden",
                            boxSizing: "border-box",
                            transition: (t) =>
                                t.transitions.create("width", {
                                    easing: t.transitions.easing.sharp,
                                    duration: t.transitions.duration.enteringScreen,
                                }),
                        },
                    }}
                >
                    {drawerNav}
                </MuiDrawer>

                <Box
                    component="main"
                    className={useAppLayout ? "pwa-main-content" : undefined}
                    sx={{
                        flexGrow: 1,
                        width: {
                            xs: "100%",
                            md: `calc(100% - ${permanentWidth}px)`,
                        },
                        transition: (t) =>
                            t.transitions.create("width", {
                                easing: t.transitions.easing.sharp,
                                duration: t.transitions.duration.enteringScreen,
                            }),
                        minWidth: 0,
                        minHeight: "100vh",
                        bgcolor: (t) =>
                            t.palette.mode === "light" ? t.palette.grey[100] : t.palette.grey[900],
                        overflow: "auto",
                        WebkitOverflowScrolling: "touch",
                        ...(useAppLayout && {
                            paddingBottom: "env(safe-area-inset-bottom)",
                            paddingLeft: "env(safe-area-inset-left)",
                            paddingRight: "env(safe-area-inset-right)",
                        }),
                    }}
                >
                    <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }} />
                    <Box
                        sx={{
                            px: { xs: 1.5, sm: 2, md: 2.5 },
                            pb: { xs: 3, md: 4 },
                            maxWidth: "100%",
                        }}
                    >
                        {children}
                    </Box>
                </Box>
            </Box>
        </DashboardLayoutContext.Provider>
    );
}
